import { produce } from 'immer'
import {
  GET_ORGANIZATION_MEMBERS_SUCCESS,
  GetRequestVirtualCardMembersActions,
} from 'src/app/RequestVirtualCard/redux/requestVirtualCard.actions'
import { CspUserListResponse } from 'src/models/cspUser.models'
import { UserListResponse } from 'src/models/user.models'
import {
  GET_CSP_ME_SUCCESS,
  GET_ME_SUCCESS,
  GetCspMeSuccessAction,
  GetMeSuccessAction,
  POST_CSP_EMULATED_SESSIONS_LIVE_SUCCESS,
  POST_CSP_EMULATED_SESSIONS_OFFLINE_SUCCESS,
  POST_CSP_LOGIN_SUCCESS,
  POST_SIGNIN_SUCCESS,
  PostCspEmulatedSessionsLiveSuccessAction,
  PostCspEmulatedSessionsOfflineSuccessAction,
  PostCspLoginSuccessAction,
  PostSigninSuccessAction,
  PUT_CSP_EMULATED_SESSIONS_REFRESH_SUCCESS,
  PUT_CSP_EMULATED_SESSIONS_VERIFY_SUCCESS,
  PutCspEmulatedSessionsRefreshSuccessAction,
  PutCspEmulatedSessionsVerifySuccessAction,
} from 'src/redux/actions/auth.actions'
import {
  GET_CREDIT_CARD_SUCCESS,
  GET_CREDIT_CARDS_SUCCESS,
  GetCreditCardsSuccessAction,
  GetCreditCardSuccessAction,
  POST_CREDIT_CARD_SUCCESS,
  PostCreditCardSuccessAction,
  PUT_CREDIT_CARD_SUCCESS,
  PutCreditCardSuccessAction,
} from 'src/redux/actions/data/creditCards.actions'
import {
  PUT_ORGANIZATION_ROLE_SUCCESS,
  PutOrganizationRoleActions,
} from 'src/redux/actions/data/organizations.actions'
import {
  GET_CSP_USER_SUCCESS,
  GET_OWN_USER_SUCCESS,
  GET_RECENT_USERS_SUCCESS,
  GET_USER_SUCCESS,
  GET_USERS_SUCCESS,
  GetCspUserSuccessAction,
  GetOwnUserActions,
  GetRecentUsersActions,
  GetUserActions,
  GetUsersSuccessAction,
  PATCH_ORG_MEMBER_SUCCESS,
  PATCH_USER_SUCCESS,
  PatchOrgMemberActions,
  PatchUserSuccessAction,
  POST_USER_AVATAR_SUCCESS,
  POST_USER_INVITES_SUCCESS,
  PostUserAvatarSuccessAction,
  PostUserInvitesSuccessAction,
} from 'src/redux/actions/data/users.actions'
import {
  GET_VIRTUAL_CARD_SUCCESS,
  GET_VIRTUAL_CARDS_SUCCESS,
  GetVirtualCardActions,
  GetVirtualCardsSuccessAction,
  POST_VIRTUAL_CARD_SUCCESS,
  PostVirtualCardSuccessAction,
} from 'src/redux/actions/data/virtualCards.actions'

export interface UsersState {
  listResponses: Record<string, UserListResponse>
  cspResponses: Record<string, CspUserListResponse>
}

export const initialState: UsersState = {
  listResponses: {},
  cspResponses: {},
}

export type Actions =
  | GetVirtualCardActions
  | PostVirtualCardSuccessAction
  | PatchUserSuccessAction
  | PostUserAvatarSuccessAction
  | GetCreditCardSuccessAction
  | GetMeSuccessAction
  | GetCspMeSuccessAction
  | PutCreditCardSuccessAction
  | PostCreditCardSuccessAction
  | PostSigninSuccessAction
  | PostCspLoginSuccessAction
  | PostCspEmulatedSessionsOfflineSuccessAction
  | PostCspEmulatedSessionsLiveSuccessAction
  | PutCspEmulatedSessionsVerifySuccessAction
  | PutCspEmulatedSessionsRefreshSuccessAction
  | PostUserInvitesSuccessAction
  | GetUsersSuccessAction
  | GetVirtualCardsSuccessAction
  | GetCreditCardsSuccessAction
  | GetCspUserSuccessAction
  | GetRecentUsersActions
  | GetOwnUserActions
  | GetUserActions
  | PutOrganizationRoleActions
  | GetRequestVirtualCardMembersActions
  | PatchOrgMemberActions

const UsersReducer = (
  state: UsersState = initialState,
  action: Actions,
): UsersState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case GET_CSP_USER_SUCCESS: {
        draft.cspResponses = {
          ...draft.cspResponses,
          [action.payload.id]: action.payload.user,
        }
        return draft
      }
      case PUT_CSP_EMULATED_SESSIONS_REFRESH_SUCCESS:
      case PUT_CSP_EMULATED_SESSIONS_VERIFY_SUCCESS:
      case POST_CSP_EMULATED_SESSIONS_LIVE_SUCCESS:
      case POST_CSP_EMULATED_SESSIONS_OFFLINE_SUCCESS: {
        draft.listResponses = {
          ...draft.listResponses,
          [action.payload.token.resource.emulatedUser.id]:
            action.payload.token.resource.emulatedUser,
          [action.payload.token.resource.user.id]:
            action.payload.token.resource.user,
        }
        return draft
      }

      case PATCH_ORG_MEMBER_SUCCESS:
      case PUT_ORGANIZATION_ROLE_SUCCESS: {
        draft.listResponses = {
          ...draft.listResponses,
          [action.payload.id]: action.payload,
        }
        return draft
      }

      case GET_ORGANIZATION_MEMBERS_SUCCESS:
      case GET_USERS_SUCCESS: {
        draft.listResponses = action.payload.users
          .map((user) => user)
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr.id]: curr,
            }),
            draft.listResponses,
          )
        return draft
      }

      case GET_RECENT_USERS_SUCCESS:
      case POST_USER_INVITES_SUCCESS: {
        draft.listResponses = action.payload.users
          .filter((user) => user !== undefined)
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr.id]: curr,
            }),
            draft.listResponses,
          )
        return draft
      }

      case POST_VIRTUAL_CARD_SUCCESS:
      case GET_VIRTUAL_CARD_SUCCESS: {
        if (action.payload.virtualCard.recipient !== undefined) {
          draft.listResponses = {
            ...draft.listResponses,
            [action.payload.virtualCard.recipient.id]:
              action.payload.virtualCard.recipient,
          }
        }
        if (action.payload.virtualCard.cardholder !== undefined) {
          draft.listResponses = {
            ...draft.listResponses,
            [action.payload.virtualCard.cardholder.id]:
              action.payload.virtualCard.cardholder,
          }
        }
        if (action.payload.virtualCard.creditCard !== undefined) {
          draft.listResponses = {
            ...draft.listResponses,
            [action.payload.virtualCard.creditCard.user.id]:
              action.payload.virtualCard.creditCard.user,
          }
        }

        return draft
      }

      case GET_USER_SUCCESS:
      case GET_OWN_USER_SUCCESS: {
        draft.listResponses = {
          [action.payload.id]: action.payload,
          ...draft.listResponses,
        }
        return draft
      }

      case GET_ME_SUCCESS:
      case PATCH_USER_SUCCESS: {
        draft.listResponses = {
          ...draft.listResponses,
          [action.payload.id]: action.payload,
        }
        return draft
      }

      case GET_CSP_ME_SUCCESS:
      case POST_SIGNIN_SUCCESS:
      case POST_USER_AVATAR_SUCCESS:
      case POST_CSP_LOGIN_SUCCESS: {
        draft.listResponses = {
          ...draft.listResponses,
          [action.payload?.user?.id]: action.payload?.user,
        }
        return draft
      }

      case GET_VIRTUAL_CARDS_SUCCESS: {
        draft.listResponses = action.payload.virtualCards.reduce(
          (acc, curr) => ({
            ...acc,
            ...(curr.cardholder !== undefined
              ? { [curr.cardholder.id]: curr.cardholder }
              : {}),
            ...(curr.recipient !== undefined
              ? { [curr.recipient.id]: curr.recipient }
              : {}),
          }),
          draft.listResponses,
        )
        return draft
      }

      case GET_CREDIT_CARDS_SUCCESS: {
        draft.listResponses = action.payload.creditCards.reduce(
          (acc, curr) => ({
            ...acc,
            ...(curr.user !== undefined ? { [curr.user.id]: curr.user } : {}),
            ...(curr.owner !== undefined
              ? { [curr.owner.id]: curr.owner }
              : {}),
          }),
          draft.listResponses,
        )
        return draft
      }

      case POST_CREDIT_CARD_SUCCESS:
      case PUT_CREDIT_CARD_SUCCESS:
      case GET_CREDIT_CARD_SUCCESS: {
        draft.listResponses = {
          ...draft.listResponses,
          ...(action.payload.creditCard.user !== undefined
            ? {
                [action.payload.creditCard.user.id]:
                  action.payload.creditCard.user,
              }
            : {}),
          ...(action.payload.creditCard.owner !== undefined
            ? {
                [action.payload.creditCard.owner.id]:
                  action.payload.creditCard.owner,
              }
            : {}),
        }
        return draft
      }
    }
  })
}

export default UsersReducer
